import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/api";
import { GraduationCap, CheckCircle2, Clock, BookOpen, ShieldCheck } from "lucide-react";

export default function Onboarding() {
  const [items, setItems] = useState([]);
  const [reader, setReader] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = () =>
    api.get("/sops", { params: { status: "Published" } })
      .then(({ data }) => setItems(data.filter((s) => s.is_onboarding)))
      .catch(() => toast.error("Failed to load your pack"))
      .finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const acknowledge = async (s) => {
    try {
      await api.post(`/sops/${s.id}/acknowledge`);
      toast.success("Signed off — thank you");
      setReader(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const done = items.filter((s) => s.acknowledged).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 100;
  const complete = items.length > 0 && done === items.length;

  return (
    <div>
      <PageHeader title="My New Starter Pack" subtitle="Read and sign off each procedure in your first week." />

      <div className="bg-white border border-border rounded-md p-5 mb-4" data-testid="onboarding-progress">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-sm bg-black text-white flex items-center justify-center shrink-0">
            <GraduationCap className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium">{done} of {items.length} completed</span>
              <span className="text-sm font-display font-bold tabular text-primary" data-testid="onboarding-percent">{pct}%</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
        {complete && (
          <div className="mt-3 text-sm text-green-700 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> All done — your onboarding pack is fully signed off.
          </div>
        )}
      </div>

      {loaded && items.length === 0 ? (
        <p className="text-center py-12 text-muted-foreground">No onboarding SOPs assigned yet.</p>
      ) : (
        <div className="bg-white border border-border rounded-md divide-y divide-border">
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-4 p-4" data-testid={`onboarding-item-${s.id}`}>
              <div className={`h-9 w-9 rounded-sm flex items-center justify-center shrink-0 ${s.acknowledged ? "bg-green-600 text-white" : "bg-secondary text-muted-foreground"}`}>
                {s.acknowledged ? <CheckCircle2 className="h-5 w-5" /> : <BookOpen className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{s.title}</div>
                <div className="text-xs text-muted-foreground">{s.category || "General"} · v{s.version}</div>
              </div>
              {s.acknowledged
                ? <Badge variant="outline" className="border-green-600 text-green-700 text-[10px] gap-1"><CheckCircle2 className="h-3 w-3" /> Signed off</Badge>
                : <span className="text-xs text-primary flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Sign-off required</span>}
              <Button size="sm" variant={s.acknowledged ? "outline" : "default"} className={s.acknowledged ? "h-8" : "h-8 bg-primary hover:bg-[#C81219] text-white"} onClick={() => setReader(s)} data-testid={`onboarding-read-${s.id}`}>
                {s.acknowledged ? "View" : "Read & Sign"}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!reader} onOpenChange={() => setReader(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {reader && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> {reader.title}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                <span>{reader.category}</span><span>Version {reader.version}</span>
                {reader.effective_date && <span>Effective {fmtDate(reader.effective_date)}</span>}
                {reader.review_date && <span>Review by {fmtDate(reader.review_date)}</span>}
              </div>
              <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed border-l-2 border-border pl-4 py-1 max-h-[50vh] overflow-y-auto">
                {reader.content || "No content."}
              </div>
              <DialogFooter className="items-center">
                {reader.acknowledged
                  ? <span className="text-sm text-green-700 flex items-center gap-1.5 mr-auto"><CheckCircle2 className="h-4 w-4" /> You have signed off this version</span>
                  : <><span className="text-xs text-muted-foreground mr-auto">By acknowledging you confirm you have read and understood this SOP.</span>
                      <Button onClick={() => acknowledge(reader)} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="onboarding-acknowledge-button"><ShieldCheck className="h-4 w-4 mr-1.5" /> I Acknowledge</Button></>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
